import Alpine from 'alpinejs'
import { initBrandFilter } from './filters/brandFilter'
import { initDropdownFilters } from './filters/dropdownFilter'
import { initCheckboxFilters } from './filters/checkboxFilter'

export const initFilters = (): void => {
    initBrandFilter()
    initDropdownFilters()
    initCheckboxFilters()

    Alpine.data('filters', () => ({
        sort: '' as string | null,
        firstLoop: false,
        isOpen: false,
        spinner: null as HTMLElement | null,
        urlParams: new URLSearchParams(window.location.search),
        filterState: {} as Record<string, string[]>,
        initialFilterState: {} as Record<string, string[]>,
        initialSort: '' as string | null,
        finalUrl: '',
        init() {
            this.spinner = this.$refs.spinner

            // Initialize filterState from URL
            Array.from(this.urlParams.keys()).forEach((key) => {
                if (key === 'sort') {
                    this.sort = this.urlParams.get(key)
                    this.initialSort = this.urlParams.get(key)
                } else if (key === 'page') {
                    // Skip page parameter
                } else {
                    // Store all values for this key
                    this.filterState[key] = this.urlParams.getAll(key)
                    this.initialFilterState[key] = this.urlParams.getAll(key)
                }
            })

            this.$watch('isOpen', (value) => {
                if (value) document.body.classList.add('no-scroll')
                else document.body.classList.remove('no-scroll')
            })

            this.$nextTick(() => {
                if (this.urlParams.size > 0) {
                    const filterAttributes: string[] = []
                    Array.from(this.urlParams).forEach(([key, _value]) => {
                        if (filterAttributes.includes(key)) return
                        filterAttributes.push(key)
                    })

                    filterAttributes.forEach((key) => {
                        if (this.urlParams.has(key)) {
                            let filterKey = key.replace('[]', '')
                            filterKey = filterKey.replace('prijs', 'price')

                            if (filterKey === 'sort') {
                                this.sort = this.urlParams.get(filterKey)
                            }

                            this.$dispatch(`toggle-collapsible-${filterKey}`)
                        }
                    })
                }
            })
        },
        applyFilters() {
            if (this.finalUrl) {
                window.location.href = this.finalUrl
            }
            this.showSpinner()
        },
        resetFilters() {
            this.resetURLKeepingSortAndPage()
            this.showSpinner()
        },
        updateSort(e: Event & { target: HTMLSelectElement }) {
            this.sort = e.target.value
            const event = new CustomEvent('update-filter', {
                detail: {
                    attribute: 'sort',
                    value: this.sort,
                    query: 'sort',
                },
            })

            window.dispatchEvent(event)
        },
        updateFilter(event: CustomEvent) {
            // this.showSpinner()
            if (event && event.detail) {
                // Update filter state in memory
                if (event.detail.attribute === 'dropdown' || event.detail.attribute === 'sort') {
                    if (event.detail.attribute === 'sort') {
                        this.sort = event.detail.value
                    } else {
                        // For dropdown filters, store as array with single value
                        this.filterState[event.detail.query] = [event.detail.value]
                    }
                } else {
                    // For array-based filters (brands, models, checkboxes)
                    if (event.detail.value && event.detail.value.length > 0) {
                        this.filterState[event.detail.query] = event.detail.value
                    } else {
                        delete this.filterState[event.detail.query]
                    }

                    // Handle subValues (e.g., removing models when brand is unchecked)
                    if (event.detail.subValues && event.detail.subValues.length > 0 && event.detail.subQuery) {
                        const currentSubValues = this.filterState[event.detail.subQuery] || []
                        this.filterState[event.detail.subQuery] = currentSubValues.filter((val: string) => !event.detail.subValues.includes(val))
                        // Remove the key if array is empty
                        if (this.filterState[event.detail.subQuery].length === 0) {
                            delete this.filterState[event.detail.subQuery]
                        }
                    }
                }

                // Build URL from filter state
                const qp = new URLSearchParams()

                // Add sort if present
                if (this.sort) {
                    qp.set('sort', this.sort)
                }

                // Remove page when filters change (except for sort)
                if (event.detail.attribute !== 'sort') {
                    // Page will be removed by not adding it
                }

                // Add all filters from state
                Object.keys(this.filterState).forEach((query) => {
                    this.filterState[query].forEach((value: string) => {
                        qp.append(query, value)
                    })
                })

                this.finalUrl = `${window.location.origin + window.location.pathname}?${qp.toString()}`
            }
        },
        showSpinner() {
            if (this.spinner) this.spinner.style.display = 'block'
        },
        checkParams() {
            return (
                this.urlParams.size > 2 ||
                (this.urlParams.size === 2 && !(this.urlParams.has('sort') && this.urlParams.has('page'))) ||
                (this.urlParams.size === 1 && !this.urlParams.has('sort') && !this.urlParams.has('page'))
            )
        },
        getAmount() {
            let count = this.urlParams.size

            if (this.urlParams.has('sort')) {
                count -= 1
            }

            if (this.urlParams.has('page')) {
                count -= 1
            }

            return count
        },
        hasSortChanged() {
            const urlSort = this.urlParams.get('sort')
            const currentSort = this.sort || ''

            // If both are empty/null, return false (no change)
            if (!urlSort && !currentSort) {
                return false
            }

            return currentSort !== urlSort
        },
        isDirty() {
            // Check if sort has changed
            const currentSort = this.sort || ''
            const initialSort = this.initialSort || ''
            if (currentSort !== initialSort) {
                return true
            }

            // Check if filters have changed
            const currentFilterKeys = Object.keys(this.filterState).sort()
            const initialFilterKeys = Object.keys(this.initialFilterState).sort()

            // Different number of filters
            if (currentFilterKeys.length !== initialFilterKeys.length) {
                return true
            }

            // Check each filter value
            for (const key of currentFilterKeys) {
                const currentValues = (this.filterState[key] || []).sort()
                const initialValues = (this.initialFilterState[key] || []).sort()

                // Different number of values
                if (currentValues.length !== initialValues.length) {
                    return true
                }

                // Check if values are different
                if (JSON.stringify(currentValues) !== JSON.stringify(initialValues)) {
                    return true
                }
            }

            // Check if any initial filters were removed
            for (const key of initialFilterKeys) {
                if (!this.filterState[key] || this.filterState[key].length === 0) {
                    return true
                }
            }

            return false
        },
        resetURLKeepingSortAndPage() {
            const url = new URL(window.location.href)
            const params = new URLSearchParams(url.search)

            const sortValue = params.get('sort')
            const pageValue = params.get('page')

            let newUrl = url.origin + url.pathname
            const newParams = new URLSearchParams()

            if (sortValue !== null) {
                newParams.set('sort', sortValue)
            }

            if (pageValue !== null) {
                newParams.set('page', pageValue)
            }

            if (newParams.toString()) {
                newUrl += `?${newParams.toString()}`
            }

            window.location.href = newUrl
        },
    }))
}
